import { useAuth0 } from '@auth0/auth0-react';
import { useSupportAccounts } from '@my/directory-api/hooks';
import { clearSupportSession } from '@my/platform/support';
import { Button, Loading } from '@my/ui';
import { Lock, LogOut } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * THE TEMPORARY GATE. My Hub is the account portal every customer will
 * eventually use to manage their own subscription; until billing exists we run
 * onboarding by hand, so only Stratum staff get in.
 *
 * <p>
 * IT IS DELIBERATELY THE ONLY PLACE THAT ASKS. Nothing below this component
 * checks for a super user — screens are account-scoped and the API authorises
 * on <c>AccountRole</c>, exactly as it will when customers arrive. Opening the
 * Hub up is then deleting this one wrapper, not unpicking a condition threaded
 * through every screen and endpoint.
 * </p>
 *
 * <p>
 * The account PICKER stays staff-only forever, and that is a different thing:
 * customers will never choose between accounts they are not members of.
 * </p>
 *
 * <p>
 * Asking the server rather than reading the claim: `/support/accounts` answers
 * 404 to anybody without the flag, so a successful load IS the answer. A client
 * that decided for itself could be wrong, and this is not a question worth
 * being wrong about.
 * </p>
 */
export function RequireSuperUser({ children }: { children: ReactNode }) {
  const { isLoading, isError } = useSupportAccounts();
  const { logout } = useAuth0();

  if (isLoading) return <Loading />;

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md rounded-lg border border-slate-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Lock className="h-6 w-6 text-slate-500" aria-hidden />
          </div>
          <h1 className="mb-2 text-lg font-semibold text-slate-900">Not available yet</h1>
          <p className="text-sm text-slate-600">
            Managing your own account and subscriptions is coming soon. In the meantime your
            Stratum contact can make any changes you need.
          </p>

          {/*
            THE ONLY WAY OUT. This screen renders before the app shell, so there
            is no sidebar and no menu — without this a customer who reaches it is
            stuck looking at it, and signing in as somebody else means clearing
            site data.
          */}
          <Button
            className="mt-6"
            variant="secondary"
            rightIcon={LogOut}
            onClick={() => {
              // Same order as everywhere else: clear before the redirect, or a
              // support session in sessionStorage outlives the sign-out and
              // greets whoever signs in next on this tab.
              clearSupportSession({ silent: true });
              logout({ logoutParams: { returnTo: window.location.origin } });
            }}
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
